import { useEffect } from 'react'
import { ROUTES } from '@/constants/corporateRoutePaths'
import { useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FormStates, StateValue } from './FormStateMachine'
import { ROUTE_TO_STATE, STATE_TO_ROUTE } from '@/utils/corporateWizard'
import { CorporateDateDetails } from './corporatedetails/CorporateDateDetails'
import { useCorpStore } from '@/reducers/corporateReducer'
import { CorporateInfo } from './corporatedetails/CorporateInfo'
import { CorporateBusinessAddress } from './corporatedetails/CorporateBusinessAddress'
import DirectorInfo from './directordetails/DirectorInfo'
import DirectorPepInfo from './directordetails/DirectorPepInfo'
import CorporateIssuerDocuments from './corporatedetails/CorporateIssuerUpload'
import CorporateArticleOfIncorporationUpload from './corporatedetails/CorporateDocuments'
import { RegistrationCompletePage } from './RegistrationCompletePage'
import CorporateAdditionalDetails from './corporatedetails/CorporateAdditionalDetails'
import DirectorPersonalShare from './directordetails/DirectorPersonalShare'
import AuthorizedDirector from './directordetails/AuthorizedDirector'
import { CorporatePageLayout } from './CorporatePageLayout'
import CorporateAssist from './corporatedetails/CorporateAssist'

export { ROUTES } from '@/constants/corporateRoutePaths'

const getFormPanel = (state: StateValue) => {
  switch (state) {
    case FormStates.CORPINFO:
      return <CorporateInfo />
    case FormStates.DIRECTOR_PERSONAL_SHARE:
      return <DirectorPersonalShare />
    case FormStates.CORP_BUS_ADDRESS:
      return <CorporateBusinessAddress />
    case FormStates.DIRECTOR_AUTH_INFO:
      return <AuthorizedDirector />
    case FormStates.CORP_ADDN_DETAILS:
      return <CorporateAdditionalDetails />
    case FormStates.CORP_DATE_DETAILS:
      return <CorporateDateDetails />
    case FormStates.CORPDOCS:
      return <CorporateIssuerDocuments />
    case FormStates.CORPORATE_ARTICLE_INCORPORATION:
      return <CorporateArticleOfIncorporationUpload />
    case FormStates.DIRECTOR_INFO:
      return <DirectorInfo />
    case FormStates.DIRECTOR_PEP_INFO:
      return <DirectorPepInfo />
    case FormStates.CORPORATE_ASSIST:
      return <CorporateAssist />
    case FormStates.COMPLETE:
      return <RegistrationCompletePage />
    default:
      return <Navigate to={STATE_TO_ROUTE[FormStates.CORPINFO]} replace />
  }
}

export const CorporateSignupPanel = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const formState = useCorpStore((state) => state.formState)
  const jumpTo = useCorpStore((state) => state.jumpTo)

  // Sync route with form state
  useEffect(() => {
    const currentState = ROUTE_TO_STATE[location.pathname]
    if (currentState && currentState !== formState) {
      jumpTo(currentState)
    }
  }, [location.pathname, formState, jumpTo])

  // Get the current panel based on form state
  const currentPanel = getFormPanel(formState)

  // Get the current step name for test ID
  const getCurrentStepName = () => {
    if (!formState) return 'unknown'
    return formState.toLowerCase().replace(/_/g, '-')
  }

  return (
    <CorporatePageLayout>
      <div data-testid={`corporate-signup-step-${getCurrentStepName()}`}>
        {currentPanel}
      </div>
    </CorporatePageLayout>
  )
}

/**
 * Code Improvement Suggestions:
 *
 * 1. The component relies on global state management through the useCorpStore hook, which could make testing and isolation difficult.
 * 2. The routing logic is complex and tightly coupled with the form state machine, consider separating these concerns.
 * 3. There's no loading state or error handling for potential async operations in child components.
 * 4. The darkmode toggle is conditionally rendered based on window.self === window.top, but lacks comments explaining this check.
 * 5. Consider using React.lazy and Suspense for code-splitting to improve initial load time for this complex multi-step form.
 * 6. The component lacks memoization which could lead to unnecessary re-renders when parent components change.
 * 7. There's no explicit accessibility considerations for screen readers navigating through a multi-step form.
 * 8. The current approach uses route-based state management which could be enhanced with a more declarative approach.
 */
